// The invitations waiting for the signed-in user, on two surfaces (#440).
//
// One component, two shapes, because they are the same fact seen from two
// places. **`"full"`** is the no-workspace screen, where the invitation is the
// entire content — before this unit that screen explained the situation and
// offered nothing, and the one action it did offer was the wrong one. **`"strip"`**
// is the quiet line inside the console for someone who already holds a workspace
// and has been asked into a second, which is the managed-provider case.
//
// **The design intent, so a later edit knows what it is keeping.** A workspace
// you are *in* is a plain row (`.choice`, the picker). An invitation is a letter
// addressed to you, and it is drawn like one: sand paper, a blue edge — the
// rail's mark, and the only edge of it in the main column — the workspace's name
// in the console's face, and the address it was sent to in the mono, small, under it. That
// address is the reason the row is visible at all (the read is authorised by the
// verified address, not by a link), so showing it is not decoration.
//
// **No token is rendered here, because none is read.** `WaitingInvitation` carries
// an `id`, the accept takes that id, and the mail's link is one of two ways in
// rather than the only one.

import { useState } from "react";

import { messageOf } from "../api/http";
import type { WaitingInvitation } from "../api/invites";
import { orgKindName } from "../api/memberships";
import { initialOf, Notice } from "./chrome";

/** Hours left, from the ISO stamp the API answers with. */
function hoursLeft(expiresAt: string, now: number): number {
  return (new Date(expiresAt).getTime() - now) / 3_600_000;
}

/**
 * How long is left, in the words a person would use.
 *
 * Deliberately coarse above a day: an invitation is a human act with a week on
 * it, and "6 days" is what someone needs to know. Under a day it gets sharper,
 * because that is when the number changes what you do next.
 */
export function deadlineOf(expiresAt: string, now: number = Date.now()): string {
  const hours = hoursLeft(expiresAt, now);
  if (hours <= 0) return "expired";
  if (hours < 1) return `expires in ${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 48) return `expires in ${Math.round(hours)} h`;
  return `expires in ${Math.round(hours / 24)} days`;
}

/** Under two days is worth saying in the accent colour; a week is not. */
export function isUrgent(expiresAt: string, now: number = Date.now()): boolean {
  const hours = hoursLeft(expiresAt, now);
  return hours > 0 && hours < 48;
}

/** What an invitation is into: a workspace, or the partner or customer above one. */
function sublineOf(invitation: WaitingInvitation): string {
  if (invitation.kind === "org") {
    return orgKindName(invitation.org_kind);
  }
  return invitation.org_name && invitation.org_name !== invitation.name
    ? `Workspace · ${invitation.org_name}`
    : "Workspace";
}

export function WaitingInvitations({
  invitations,
  address,
  error,
  onAccept,
  shape,
}: {
  invitations: WaitingInvitation[];
  /** The signed-in address. Shown because it is what authorises the read. */
  address: string;
  error: string | null;
  onAccept: (inviteId: string) => Promise<void>;
  shape: "full" | "strip";
}) {
  if (invitations.length === 0) return error === null ? null : <Notice tone="bad">{error}</Notice>;
  if (shape === "strip") {
    return (
      <section className="invite-strip" aria-label="Invitations waiting for you">
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        {invitations.map((invitation) => (
          <InvitationLine key={invitation.id} invitation={invitation} onAccept={onAccept} />
        ))}
      </section>
    );
  }
  return (
    <>
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      <ul className="invitations" aria-label="Invitations waiting for you">
        {invitations.map((invitation) => (
          <li key={invitation.id}>
            <InvitationCard invitation={invitation} address={address} onAccept={onAccept} />
          </li>
        ))}
      </ul>
    </>
  );
}

/** The act, and the refusal beside the card that was clicked. */
function useAccept(inviteId: string, onAccept: (inviteId: string) => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const accept = async () => {
    setBusy(true);
    setFailed(null);
    try {
      await onAccept(inviteId);
    } catch (error) {
      setFailed(messageOf(error));
    } finally {
      // The component is still mounted on a refusal, and on success the screen
      // it is on has been replaced by the workspace it just joined.
      setBusy(false);
    }
  };
  return { busy, failed, accept };
}

function InvitationCard({
  invitation,
  address,
  onAccept,
}: {
  invitation: WaitingInvitation;
  address: string;
  onAccept: (inviteId: string) => Promise<void>;
}) {
  const { busy, failed, accept } = useAccept(invitation.id, onAccept);
  const name = invitation.name ?? invitation.workspace_id ?? invitation.org_id ?? "a workspace";
  const urgent = isUrgent(invitation.expires_at);
  return (
    // The staggered reveal is the sheet's, by `:nth-child`, not an inline
    // `animationDelay`: `styles.test.ts` forbids a `style=` object in a
    // component, and the rule is right — the delays are part of the look.
    <article className="invitation rise">
      <span className="initial initial--mark" aria-hidden="true">
        {initialOf(name)}
      </span>
      <div className="invitation__body">
        <h2 className="invitation__name">{name}</h2>
        <p className="invitation__sub">{sublineOf(invitation)}</p>
        {/* The role tag sits at the end of the sentence rather than inside it.
            `.tag` is an inline-flex box with 8px of side padding, so a comma
            straight after one reads as "admin , invited by" — the padding, not
            a stray space. Nothing punctuates it here. */}
        <p className="invitation__who">
          Invited by <strong>{invitation.invited_by}</strong> as{" "}
          <span className="tag">{invitation.role}</span>
        </p>
        {/* The address the invitation was sent to. Not a detail: it is the fact
            that authorises this row being on the screen at all — the read is
            keyed on a verified address, not on a link someone was handed. */}
        <p className="invitation__address">sent to {address}</p>
        {failed === null ? null : <Notice tone="bad">{failed}</Notice>}
      </div>
      <div className="invitation__act">
        <button type="button" className="btn btn--primary" onClick={() => void accept()} disabled={busy}>
          {busy ? "Joining…" : "Accept"}
        </button>
        <small className={urgent ? "invitation__deadline is-urgent" : "invitation__deadline"}>
          {deadlineOf(invitation.expires_at)}
        </small>
      </div>
    </article>
  );
}

/** The strip's one line: the same facts, folded onto a sentence. */
function InvitationLine({
  invitation,
  onAccept,
}: {
  invitation: WaitingInvitation;
  onAccept: (inviteId: string) => Promise<void>;
}) {
  const { busy, failed, accept } = useAccept(invitation.id, onAccept);
  const name = invitation.name ?? "a workspace";
  return (
    <div className="invite-strip__row rise">
      <span className="invite-strip__mark" aria-hidden="true" />
      <p className="invite-strip__text">
        <strong>{invitation.invited_by}</strong> has invited you to{" "}
        <strong>{name}</strong> as <span className="tag">{invitation.role}</span>
        <small className={isUrgent(invitation.expires_at) ? "is-urgent" : undefined}>
          {" · "}
          {deadlineOf(invitation.expires_at)}
        </small>
      </p>
      {failed === null ? null : <Notice tone="bad">{failed}</Notice>}
      <button type="button" className="btn" onClick={() => void accept()} disabled={busy}>
        {busy ? "Joining…" : "Accept"}
      </button>
    </div>
  );
}
